import React, { useState, useEffect } from "react";
import { Box, Card, Typography, Button, TextField, Alert, CircularProgress, InputAdornment, IconButton } from "@mui/material";
import PasswordStrengthBar from "../components/PasswordStrengthBar";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { validateEmail } from "../utils/validate";
import { Colors } from "../utils/colors";
import { useNavigate } from "react-router-dom";
import { AuthService } from "../services/auth/Auth";
import { useDispatch } from "react-redux";
import { setIsAuthenticated,setUser,setSessionExpired } from "../Redux/Reducers/appState";
import axios from "axios"
import { consumeUserSessionMessage } from "../utils/userSessionTimeout";
import { clearPendingPaystackPayment } from "../utils/pendingPaystackPayment";


const Login = () => {
  const [ipaddress, setIPAdress] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    ipaddress: ipaddress
  });

  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  

  const dispatch = useDispatch();
  const navigate = useNavigate();

    const update = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

    useEffect(() => { 
     
    const fetchIpAddress = async()=>{
       try { 
         const res = await axios.get('https://ipapi.co/json/')
        setIPAdress(res.data.ip); 
       } catch (error) { 
          alert(error)
        } 
        }
         fetchIpAddress(); 
         }, []); 

  useEffect(() => {
    const message = consumeUserSessionMessage();
    if (message) {
      setServerError(message);
    }
  }, []);

  const SignIn = async () => {
    setLoading(true);
    setServerError("");
    
    try {
      const res = await AuthService.login(formData);

      if (!res.status) {
        setServerError(res.message || "Login failed");
        setLoading(false);
        return;
      }

      dispatch(setUser(res.user));
      dispatch(setIsAuthenticated(res.status));
      dispatch(setSessionExpired(false));
      clearPendingPaystackPayment();

      setLoading(false);
      const returnTo = new URLSearchParams(window.location.search).get("returnTo");
      const invitationPath = /^\/influencer\/invitation\/[a-f0-9]{64}$/i.test(returnTo || "") ? returnTo : "/";
      navigate(invitationPath, { replace: true });

    } catch (error) {
      console.log(error);
      setServerError("Unable to connect to server. Try again.");
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      !validateEmail(formData.email) ||
      formData.password.length < 5 ||
      loading
    ) {
      return;
    }

    await SignIn();
  };

  return (
    <Box
      sx={{
        minHeight: "50vh",
        p: 3,
        color: "#fff",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Card
        component="form"
        onSubmit={handleSubmit}
        sx={{ p: 4, borderRadius: 1, width: "100%", maxWidth: 500 }}
      >

        <Typography variant="h5" sx={{ color: Colors.accent }} fontWeight={700}>
          Login
        </Typography>

        
        {serverError && (
          <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
            {serverError}
          </Alert>
        )}

        
        <TextField
          label="Email Address"
          fullWidth
          sx={inputStyle}
          value={formData.email}
          error={!!formData.email && !validateEmail(formData.email)}
          helperText={
            formData.email && !validateEmail(formData.email)
              ? "Invalid email format"
              : ""
          }
          onChange={(e) => update("email", e.target.value)}
        />

        
        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          fullWidth
          sx={inputStyle}
          value={formData.password}
          onChange={(e) => update("password", e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end" sx={{ color: "white" }}>
                  {showPassword ? <VisibilityOffIcon /> : <RemoveRedEyeIcon />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <PasswordStrengthBar password={formData.password} />

        <Typography
  sx={{
    mt: 1,
    textAlign: "right",
    fontSize: 13,
    color: Colors.accent,
    cursor: "pointer",
    fontWeight: 600
  }}
  onClick={() => navigate("/forgot-password")}
>
  Forgot Password?
</Typography>

        
        <Button
          fullWidth
          type="submit"
          sx={{ mt: 3, backgroundColor: Colors.secondary, color: "white" }}
          variant="contained"
          disabled={
            !validateEmail(formData.email) ||
            formData.password.length < 5 ||
            loading
          }
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "white" }} />
          ) : (
            "Login"
          )}
        </Button>

        
        <Typography
          sx={{
            mt: 2,
            textAlign: "center",
            color: Colors.muted,
            fontSize: 14,
          }}
        >
          Don’t have an account?{" "}
          <span
            style={{
              color: Colors.accent,
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 600,
            }}
            onClick={() => navigate("/register")}
          >
            Register here
          </span>
        </Typography>
      </Card>
    </Box>
  );
};


const inputStyle =  {
            mt: 2,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: Colors.accent,
              },
              "&:hover fieldset": {
                borderColor: Colors.accent,
              },
              "&.Mui-focused fieldset": {
                borderColor: Colors.accent,
              },
            },
            "& .MuiInputLabel-root": {
              color: Colors.accent,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: Colors.accent,
            },
            input: {
              color: "whitesmoke",
            },
          }

export default Login;
